import { inject, Service, signal } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { Layout, MedinboxConfiguration } from '@shared/models/equipment';
import { WebSocketMessage, EquipmentPing, InboundHandlers, InboundWsMessage, dispatchInbound, isAction } from '@shared/models/websocket';
import { CognitoUser } from '@core/user/cognito-user';
import { WebSocketClient } from '../realtime/web-socket-client';
import { ScaleFrame, VideoSizeAndPosition, SourceCapture, SourceMaskWithUser } from '@shared/models/live';
import { WebSocketActionEnum } from '@shared/enums/websocket';
import { Loader } from '../ui/loader';
import { Recovery } from './recovery';
import { WorkerBusy } from './worker-busy';
import { EquipmentCommand } from '../realtime/equipment-command';
import { LiveCommand } from '../realtime/live-command';
import { UserFeedback } from '../ui/user-feedback';
import { syncState } from '@shared/utils/sync-state';
import { STORAGE_KEYS } from '@shared/constants/storage-keys';

@Service()
export class Medinbox {
  private readonly liveCommands = inject(LiveCommand);
  private readonly equipmentCommands = inject(EquipmentCommand);
  private webSocketService = inject(WebSocketClient);
  private userService = inject(CognitoUser);
  private loader = inject(Loader);
  private readonly recoveryService = inject(Recovery);
  private readonly workerBusyService = inject(WorkerBusy);
  private readonly messages = inject(UserFeedback);

  public ratio = {
    defaultWidth: 1920,
    defaultHeight: 1080,
  };

  private readonly equipmentId = syncState<string | null>(null);
  public readonly medinboxId = this.equipmentId.value;
  public readonly medinboxId$ = this.equipmentId.changes$;
  private readonly layout = syncState<Layout | null>(null);
  public readonly currentLayout = this.layout.value;
  public readonly currentLayout$ = this.layout.changes$;
  private readonly configuration = syncState<MedinboxConfiguration | null>(null);
  public readonly medinboxConfiguration = this.configuration.value;
  public readonly medinboxConfiguration$ = this.configuration.changes$;
  private readonly frames = syncState<ScaleFrame[]>([]);
  public readonly scaledFrames = this.frames.value;
  public readonly scaledFrames$ = this.frames.changes$;
  public sourceCapture$: BehaviorSubject<SourceCapture | null> = new BehaviorSubject<SourceCapture | null>(null);
  public sourceMaskWithUser$: BehaviorSubject<SourceMaskWithUser | null> = new BehaviorSubject<SourceMaskWithUser | null>(null);
  private readonly recordElapsed = signal(0);
  public readonly recordElapsedSeconds = this.recordElapsed.asReadonly();
  private readonly selectedSource = syncState<number | null>(null);
  public readonly selectedSourceId = this.selectedSource.value;
  public readonly selectedSourceId$ = this.selectedSource.changes$;

  private readonly equipmentActions: InboundHandlers = {
    [WebSocketActionEnum.CONFIGURATION_LOADED]: (message) => this.setConfiguration(message.message.params ?? null),
    [WebSocketActionEnum.CONFIGURATION_CURRENT]: (message) => this.layout.set(message.message.params ?? null),
    [WebSocketActionEnum.RESULT]: (message) => this.onResult(message),
    [WebSocketActionEnum.PING]: (message) => this.onPing(message),
    [WebSocketActionEnum.MASK_IMAGE]: (message) => this.sourceCapture$.next(message.message.params),
    // Plain data on the wire, not SourceMask instances.
    [WebSocketActionEnum.GET_MASK]: (message) => this.sourceMaskWithUser$.next(message.message.params as SourceMaskWithUser),
    [WebSocketActionEnum.RECORD_TIME]: (message) => this.recordElapsed.set(message.message.params),
    [WebSocketActionEnum.CAN_RECOVER]: () => this.recoveryService.setCanRecover(true),
  };

  constructor() {
    this.webSocketService.medinboxMessage$.subscribe((message) => {
      if (!message) return;
      if (message.sourceId !== this.equipmentId.get()) return;
      if (isAction(message, WebSocketActionEnum.CHANGE_SOURCE) || isAction(message, WebSocketActionEnum.FRAME_FULLSCREEN)) {
        applyLayoutChangeResult(message, this, this.messages, this.isForCurrentUser(message));
      } else if (message.audience === 'EQUIPMENT') {
        this.handleWebsocketMessage(message);
      }
    });
  }

  /** Saved to localStorage before anyone hears of it; null keeps the saved id. */
  setMedinboxId(medinboxId: string | null): void {
    if (medinboxId != null) {
      localStorage.setItem(STORAGE_KEYS.EQUIPMENT_ID, medinboxId);
    }
    this.equipmentId.set(medinboxId);
  }

  /** The ratio is updated before anyone hears of the configuration; null keeps it. */
  setConfiguration(configuration: MedinboxConfiguration | null): void {
    if (configuration != null) {
      this.ratio.defaultHeight = configuration.Multiviewer.Format.Resolution.Height ?? 1080;
      this.ratio.defaultWidth = configuration.Multiviewer.Format.Resolution.Width ?? 1920;
    }
    this.configuration.set(configuration);
  }

  /** Notifies even when the layout is unchanged. */
  setLayout(layout: Layout | null): void {
    this.layout.set(layout);
  }

  selectSource(sourceId: number | null): void {
    this.selectedSource.set(sourceId);
  }

  private isForCurrentUser(message: WebSocketMessage): boolean {
    return message.audience !== 'USER' || message.destinationId.toLowerCase() === this.userService.getCurrentUserId().toLowerCase();
  }

  private handleWebsocketMessage(message: WebSocketMessage) {
    dispatchInbound(this.equipmentActions, message);
  }

  // First match wins: a result carrying a layout never triggers the recording branch.
  private onResult(message: InboundWsMessage<WebSocketActionEnum.RESULT>) {
    if (message.message.params.actionExecuted && message.message.params.layout) {
      const layout = message.message.params.layout;
      this.layout.set(layout);
    } else if (message.message.params.actionExecuted && typeof message.message.params.Recording === 'boolean') {
      void this.liveCommands.send(
        WebSocketActionEnum.RECORD,
        { start: message.message.params.Recording },
        { sourceId: this.userService.getCurrentUserId(), destinationId: message.liveCode },
      );
      this.loader.hide();
    }
  }

  private onPing(message: InboundWsMessage<WebSocketActionEnum.PING>) {
    // The box channel only carries the equipment ping.
    if (this.workerBusyService.isBusy() !== (message.message.params as EquipmentPing).isBusy)
      this.workerBusyService.updateBusyState((message.message.params as EquipmentPing).isBusy);
    void this.equipmentCommands.sendOutsideLive(WebSocketActionEnum.PONG, undefined, {
      sourceId: this.userService.getCurrentUserId(),
      equipmentId: this.equipmentId.get(),
    });
  }

  public async initMedinboxChannelAndGetCurrentConfiguration(liveCode: string, fetchConfiguration = true) {
    await this.webSocketService.initMedinboxChannel(this.equipmentId.get() as string, true);
    // GET_CONFIGURATION is only allowed for camera/layout rights; skip it when the socket is opened for recording only
    if (!fetchConfiguration) {
      return;
    }
    await this.equipmentCommands.send(WebSocketActionEnum.GET_CONFIGURATION, undefined, {
      sourceId: this.userService.getCurrentUserId(),
      equipmentId: this.equipmentId.get(),
      liveCode,
    });
  }

  /** Equipment pages: opens the box channel once, falling back to the equipment id saved in localStorage. */
  ensureEquipmentChannel(): void {
    if (!this.webSocketService.hasMedinboxChannel && (this.equipmentId.get() || localStorage.getItem(STORAGE_KEYS.EQUIPMENT_ID))) {
      let currentEquipmentId = this.equipmentId.get();
      if (!currentEquipmentId) {
        currentEquipmentId = localStorage.getItem(STORAGE_KEYS.EQUIPMENT_ID);
        this.setMedinboxId(currentEquipmentId);
      }
      if (currentEquipmentId) {
        void this.webSocketService.initMedinboxChannel(currentEquipmentId);
      }
    }
  }

  /** Always re-emits, even the unchanged layout: live-video-content's combineLatest waits for it. */
  ensureLayout(fallback: Layout): void {
    this.layout.set(this.layout.get() ?? fallback);
  }

  getNumberOfLayouts(): Observable<number> {
    return this.medinboxConfiguration$.pipe(
      map((mc) => {
        return mc?.Multiviewer.Layouts.length ?? 0;
      }),
    );
  }

  getNumberOfSources(): Observable<number> {
    return this.medinboxConfiguration$.pipe(
      map((mc) => {
        return mc?.Sources.length ?? 0;
      }),
    );
  }

  public calculateScaledFrame(video: VideoSizeAndPosition | null) {
    const frames: ScaleFrame[] = [];
    const currentLayout = this.layout.get();
    const videoSize = video;
    if (videoSize && currentLayout) {
      currentLayout?.Frames.forEach((f) => {
        const currentSource = this.configuration.get()?.Sources.find((s) => s.Id === f.CurrentSourceId);
        const scaledFrame: ScaleFrame = {
          id: f.Id,
          isCamera: currentSource?.IsCamera ?? false,
          sourceId: currentSource?.Id ?? f.DefaultSourceId,
          width: (f.Width * videoSize.width) / this.ratio.defaultWidth,
          height: (f.Height * videoSize.height) / this.ratio.defaultHeight,
          startX: (f.X * videoSize.width) / this.ratio.defaultWidth + videoSize.x,
          startY: (f.Y * videoSize.height) / this.ratio.defaultHeight + videoSize.y,
          icon: currentSource?.Icon ?? '',
        };
        frames.push(scaledFrame);
      });
      this.frames.set(frames);
    }
  }

  public getHeadsetColorFromConfig(headsetId: number): string {
    const conf = this.configuration.get();
    const headsetfound = conf?.Headsets.find((h) => h.Id === headsetId);
    return headsetfound?.Color.toLowerCase() ?? 'no-color';
  }
}

/** Outcome of a CHANGE_SOURCE / FRAME_FULLSCREEN answer; `notify` false applies the layout without any toast. */
export function applyLayoutChangeResult(
  mess: InboundWsMessage<WebSocketActionEnum.CHANGE_SOURCE | WebSocketActionEnum.FRAME_FULLSCREEN>,
  medinbox: Pick<Medinbox, 'setLayout'>,
  messages: Pick<UserFeedback, 'showError' | 'showWarning'>,
  notify = true,
): void {
  if (!mess.message.params.actionExecuted) {
    if (notify) messages.showError(mess.message.params.error as string);
  } else {
    const layout = mess.message.params.layout as Layout;
    medinbox.setLayout(layout);
    if (notify && mess.message.params.message.indexOf('unreachable') !== -1) {
      messages.showWarning(mess.message.params.message);
    }
  }
}
